import React, { FC } from 'react';
import { FaChevronDown } from 'react-icons/fa';
import { actionItemDisplayText, actionItemQuestionText } from '../../utils/actionItemDisplayText';
import {
	AutopilotStructuredPreview,
	BidderActionItemMessageBatchPreview,
	BidderActionItemMessageItem,
	BidderActionItemMessagePackageChoice,
	BidderActionItemMessagePackageVariant,
	BidderActionItemMessageReadiness,
	BidderActionItemMessageRow,
	BidderActionItemMessageSourceMode,
} from './types';

interface Props {
	disabled?: boolean;
	editable?: boolean;
	onChange?: (preview: BidderActionItemMessageBatchPreview) => void;
	preview: BidderActionItemMessageBatchPreview;
}

const packageChoiceId = (choice: BidderActionItemMessagePackageChoice) =>
	String(choice.bidPackageId ?? choice.id ?? '');

const packageChoiceLabel = (choice: BidderActionItemMessagePackageChoice) =>
	String(
		choice.label || choice.packageName || choice.name || `Bid package ${packageChoiceId(choice)}`,
	);

const bidderLabel = (row: BidderActionItemMessageRow, index: number) =>
	String(
		row.bidderCompanyName || row.bidderName || row.title || row.label || `Bidder ${index + 1}`,
	);

const readinessLabel = (readiness?: string) => {
	if (readiness === 'READY') return 'Ready';
	if (readiness === 'STALE') return 'Stale';
	return 'Needs attention';
};

const actionItemQuestion = (item: BidderActionItemMessageItem) =>
	actionItemQuestionText(item, 'Please clarify this item.');

const numberedQuestionPattern = /^\s*\d+[.)]\s+/;
const closingPattern = /^\s*(?:thank you|thanks|regards|sincerely)(?:[,\s]|$)/i;

interface MessageBodyFrame {
	intro: string[];
	outro: string[];
	questions: string[][];
}

const trimBlankEdges = (lines: string[]) => {
	let start = 0;
	let end = lines.length;
	while (start < end && !lines[start].trim()) start += 1;
	while (end > start && !lines[end - 1].trim()) end -= 1;
	return lines.slice(start, end);
};

/**
 * Splits a generated bidder message at its numbered question boundaries. The
 * question blocks are deliberately structural: they are never rediscovered by
 * fuzzy text matching after the user changes a selection.
 */
const messageBodyFrame = (body: string): MessageBodyFrame => {
	const lines = String(body || '').split(/\r?\n/);
	const questionStarts = lines.reduce<number[]>((indexes, line, index) => {
		if (numberedQuestionPattern.test(line)) indexes.push(index);
		return indexes;
	}, []);
	if (!questionStarts.length) {
		const closingIndex = lines.findIndex((line) => closingPattern.test(line));
		return {
			intro: closingIndex >= 0 ? lines.slice(0, closingIndex) : lines,
			outro: closingIndex >= 0 ? lines.slice(closingIndex) : [],
			questions: [],
		};
	}
	const closingIndex = lines.findIndex(
		(line, index) => index > questionStarts[questionStarts.length - 1] && closingPattern.test(line),
	);
	const questionEnd = closingIndex >= 0 ? closingIndex : lines.length;
	return {
		intro: lines.slice(0, questionStarts[0]),
		outro: closingIndex >= 0 ? lines.slice(closingIndex) : [],
		questions: questionStarts.map((start, index) => {
			const nextStart = questionStarts[index + 1] ?? questionEnd;
			return trimBlankEdges(lines.slice(start, Math.min(nextStart, questionEnd)));
		}),
	};
};

const stripQuestionNumber = (lines: string[]) => {
	if (!lines.length) return [];
	return [lines[0].replace(numberedQuestionPattern, ''), ...lines.slice(1)];
};

const generatedQuestionSegments = (
	defaultBody: string,
	items: BidderActionItemMessageItem[],
): Map<string, string[]> => {
	const frame = messageBodyFrame(defaultBody);
	return new Map(
		items.map((item, index) => {
			const fromBody = frame.questions.length === items.length ? frame.questions[index] : null;
			const fallback = actionItemQuestion(item);
			let segment: string[] = [];
			if (fromBody && fromBody.length) segment = stripQuestionNumber(fromBody);
			else if (fallback) segment = [fallback];
			return [String(item.id), segment];
		}),
	);
};

const safeEditableFrame = (currentBody: string, defaultBody: string): MessageBodyFrame => {
	const current = messageBodyFrame(currentBody);
	const fallback = messageBodyFrame(defaultBody);
	if (!current.questions.length) {
		return {
			intro: trimBlankEdges(current.intro).length ? current.intro : fallback.intro,
			outro: current.outro.length ? current.outro : fallback.outro,
			questions: [],
		};
	}
	return {
		intro: current.intro,
		outro: current.outro.length ? current.outro : fallback.outro,
		questions: current.questions,
	};
};

const joinMessageSections = (sections: string[][]) =>
	sections
		.map(trimBlankEdges)
		.filter((section) => section.length)
		.map((section) => section.join('\n'))
		.join('\n\n');

const textLines = (value: string) => String(value || '').split(/\r?\n/);

const actionItemsWithBodyMessages = (
	items: BidderActionItemMessageItem[],
	body: string,
): BidderActionItemMessageItem[] => {
	const selectedItems = items.filter((item) => item.selected !== false);
	const frame = messageBodyFrame(body);
	if (frame.questions.length !== selectedItems.length) return items;
	const messagesById = new Map(
		selectedItems.map((item, index) => [
			String(item.id),
			stripQuestionNumber(frame.questions[index]).join('\n').trim(),
		]),
	);
	return items.map((item) => {
		const messageText = messagesById.get(String(item.id));
		return messageText ? { ...item, messageText } : item;
	});
};

export const composeBidderActionItemBody = ({
	intro,
	outro,
	selectedItems,
}: {
	intro: string[];
	outro: string[];
	selectedItems: BidderActionItemMessageItem[];
}): string => {
	const questions = selectedItems
		.map((item) => actionItemQuestion(item))
		.filter(Boolean)
		.map((question, index) => {
			const lines = textLines(question);
			return [`${index + 1}. ${lines[0]}`, ...lines.slice(1)];
		});
	return joinMessageSections([intro, ...questions, outro]);
};

export const reconcileBidderActionItemBody = ({
	allItems,
	currentBody,
	defaultBody,
	preserveEdits,
	previouslySelectedItems,
	selectedItems,
}: {
	allItems: BidderActionItemMessageItem[];
	currentBody: string;
	defaultBody: string;
	preserveEdits: boolean;
	previouslySelectedItems?: BidderActionItemMessageItem[];
	selectedItems: BidderActionItemMessageItem[];
}): string => {
	const canonicalBody = String(defaultBody || currentBody || '');
	const frame = preserveEdits
		? safeEditableFrame(String(currentBody || ''), canonicalBody)
		: messageBodyFrame(canonicalBody);
	const generatedSegments = generatedQuestionSegments(canonicalBody, allItems);
	const priorItems = previouslySelectedItems || allItems.filter((item) => item.selected !== false);
	const editedSegments = new Map<string, string[]>();
	if (preserveEdits && frame.questions.length === priorItems.length) {
		priorItems.forEach((item, index) => {
			editedSegments.set(String(item.id), stripQuestionNumber(frame.questions[index]));
		});
	}
	const selectedSegments = selectedItems
		.map(
			(item) => editedSegments.get(String(item.id)) || generatedSegments.get(String(item.id)) || [],
		)
		.filter((segment) => segment.length)
		.map((segment, index) => [`${index + 1}. ${segment[0]}`, ...segment.slice(1)]);
	return joinMessageSections([frame.intro, ...selectedSegments, frame.outro]);
};

const sourceModeDefaultReason = (mode: BidderActionItemMessageSourceMode) =>
	mode === 'RESEND_LAST'
		? 'Uses the last successfully sent clarification for this bidder.'
		: "Uses this bidder's latest unresolved Bid Leveling action items.";

const sourceModeUnavailableReasonCode = (mode: BidderActionItemMessageSourceMode) =>
	mode === 'RESEND_LAST' ? 'PRIOR_CLARIFICATION_UNAVAILABLE' : 'NO_UNRESOLVED_ACTION_ITEMS';

interface BidderActionItemMessageSourceState {
	readiness: BidderActionItemMessageReadiness;
	reasonCodes: string[];
	sourceModeReason: string;
}

export const bidderActionItemMessageSourceState = (
	row: BidderActionItemMessageRow,
	mode: BidderActionItemMessageSourceMode = row.sourceMode || 'SEND_CURRENT',
	preferSourceOption = false,
): BidderActionItemMessageSourceState => {
	const sourceOption = row.sourceOptions?.[mode];
	const optionReadiness = sourceOption?.readiness;
	const hasOptionState =
		optionReadiness === 'READY' ||
		optionReadiness === 'STALE' ||
		optionReadiness === 'NEEDS_ATTENTION' ||
		preferSourceOption;
	let readiness: BidderActionItemMessageReadiness = row.readiness || 'READY';
	let reasonCodes = Array.isArray(row.reasonCodes) ? row.reasonCodes.map(String) : [];
	if (hasOptionState) {
		readiness = optionReadiness || 'READY';
		reasonCodes = Array.isArray(sourceOption?.reasonCodes)
			? (sourceOption?.reasonCodes || []).map(String)
			: [];
	}
	if (sourceOption?.available === false) {
		readiness = 'NEEDS_ATTENTION';
		if (!reasonCodes.length) reasonCodes = [sourceModeUnavailableReasonCode(mode)];
	}
	let sourceModeReason = row.sourceModeReason || sourceModeDefaultReason(mode);
	if (hasOptionState) sourceModeReason = sourceModeDefaultReason(mode);
	if (sourceOption?.sourceModeReason) sourceModeReason = sourceOption.sourceModeReason;
	return {
		readiness,
		reasonCodes,
		sourceModeReason,
	};
};

export const bidderActionItemMessageBatchIssue = (
	preview: BidderActionItemMessageBatchPreview,
): string | null => {
	if (!preview.bidPackageId) return 'Select a bid package before approving.';
	if (preview.readiness && preview.readiness !== 'READY') {
		return `${preview.packageName || 'The selected bid package'} must be ready before approval.`;
	}
	const selectedRows = (preview.rows || []).filter((row) => row.selected !== false);
	if (!selectedRows.length) return 'Select at least one bidder before approving.';
	for (const row of selectedRows) {
		const name = bidderLabel(row, preview.rows.indexOf(row));
		const sourceMode = row.sourceMode || 'SEND_CURRENT';
		const sourceOption = row.sourceOptions?.[sourceMode];
		const sourceState = bidderActionItemMessageSourceState(row, sourceMode);
		if (sourceOption && sourceOption.available === false) {
			return `${readinessLabel(
				sourceState.readiness,
			)}: the selected message source is unavailable for ${name}.`;
		}
		if (sourceState.readiness !== 'READY') {
			return `${name} must be ready or removed from this batch before approval.`;
		}
		if (!(row.authorizedContacts || []).some((contact) => contact.selected !== false)) {
			return `Select at least one authorized contact for ${name}.`;
		}
		if (!String(row.subject || '').trim() || !String(row.body || '').trim()) {
			return `Enter both a subject and message for ${name}.`;
		}
		if (
			row.sourceMode === 'SEND_CURRENT' &&
			!(row.actionItems || []).some((item) => item.selected !== false)
		) {
			return `Select at least one current action item for ${name}.`;
		}
	}
	return null;
};

export const bidderActionItemMessageItemMessages = (
	row: BidderActionItemMessageRow,
): Array<{ id: number | string; messageText: string }> => {
	const actionItems = actionItemsWithBodyMessages(row.actionItems || [], String(row.body || ''));
	return actionItems
		.filter((item) => item.selected !== false)
		.map((item) => ({
			id: item.id,
			messageText: actionItemQuestion(item),
		}));
};

export const bidderActionItemMessageBatchApprovalPayload = (
	preview: BidderActionItemMessageBatchPreview,
): AutopilotStructuredPreview => ({
	kind: 'BIDDER_ACTION_ITEM_MESSAGE_BATCH',
	bidPackageId: preview.bidPackageId,
	rows: (preview.rows || []).map((row) => ({
		id: row.id,
		selected: row.selected !== false,
		sourceMode: row.sourceMode || 'SEND_CURRENT',
		selectedContactIds: (row.authorizedContacts || [])
			.filter((contact) => contact.selected !== false)
			.map((contact) => contact.id),
		subject: String(row.subject || ''),
		body: String(row.body || ''),
		selectedActionItemIds: (row.actionItems || [])
			.filter((item) => item.selected !== false)
			.map((item) => item.id),
		itemMessages:
			(row.sourceMode || 'SEND_CURRENT') === 'SEND_CURRENT'
				? bidderActionItemMessageItemMessages(row)
				: [],
	})),
});

const BidderActionItemMessageBatch: FC<Props> = ({
	disabled = false,
	editable = false,
	onChange,
	preview,
}) => {
	const rows = Array.isArray(preview.rows) ? preview.rows : [];
	const packageChoices = Array.isArray(preview.packageChoices) ? preview.packageChoices : [];
	const packageVariants = Array.isArray(preview.packageVariants) ? preview.packageVariants : [];
	const canSwitchPackage = packageChoices.length > 1 && packageVariants.length > 1;
	const updatePreview = (next: BidderActionItemMessageBatchPreview) => {
		if (editable && onChange) onChange(next);
	};
	const variantsWithCurrentRows = (nextRows: BidderActionItemMessageRow[]) =>
		packageVariants.map((variant) =>
			String(variant.bidPackageId) === String(preview.bidPackageId)
				? { ...variant, rows: nextRows }
				: variant,
		);
	const updateRow = (rowIndex: number, patch: Partial<BidderActionItemMessageRow>) => {
		const nextRows = rows.map((row, index) => (index === rowIndex ? { ...row, ...patch } : row));
		updatePreview({
			...preview,
			rows: nextRows,
			packageVariants: variantsWithCurrentRows(nextRows),
		});
	};
	const selectPackage = (bidPackageId: string) => {
		const variant = packageVariants.find(
			(candidate) => String(candidate.bidPackageId) === String(bidPackageId),
		);
		if (!variant) return;
		const preservedVariants = variantsWithCurrentRows(rows);
		const selectedVariant = preservedVariants.find(
			(candidate) => String(candidate.bidPackageId) === String(bidPackageId),
		) as BidderActionItemMessagePackageVariant | undefined;
		const nextVariant = selectedVariant || variant;
		updatePreview({
			...preview,
			bidPackageId: nextVariant.bidPackageId,
			packageName: nextVariant.packageName,
			matchingBidderCount: nextVariant.matchingBidderCount,
			readiness: nextVariant.readiness,
			reasonCodes: nextVariant.reasonCodes,
			rows: nextVariant.rows.map((row) => ({ ...row })),
			packageChoices: packageChoices.map((choice) => ({
				...choice,
				selected: packageChoiceId(choice) === String(nextVariant.bidPackageId),
			})),
			packageVariants: preservedVariants,
		});
	};
	const updateCurrentMessage = (
		rowIndex: number,
		row: BidderActionItemMessageRow,
		patch: {
			actionItems?: BidderActionItemMessageItem[];
			intro?: string[];
			outro?: string[];
		},
	) => {
		const defaultOption = row.sourceOptions?.[row.sourceMode || 'SEND_CURRENT'];
		const frame = safeEditableFrame(
			String(row.body || ''),
			String(defaultOption?.body || row.body || ''),
		);
		const currentItems = actionItemsWithBodyMessages(row.actionItems || [], String(row.body || ''));
		const actionItems = patch.actionItems || currentItems;
		updateRow(rowIndex, {
			actionItems,
			body: composeBidderActionItemBody({
				intro: patch.intro || frame.intro,
				outro: patch.outro || frame.outro,
				selectedItems: actionItems.filter((item) => item.selected !== false),
			}),
			bodyEdited: true,
		});
	};
	const selectSourceMode = (rowIndex: number, mode: BidderActionItemMessageSourceMode) => {
		const row = rows[rowIndex];
		const previousMode = row.sourceMode || 'SEND_CURRENT';
		const sourceOptions = { ...(row.sourceOptions || {}) };
		const previousOption = sourceOptions[previousMode];
		if (previousOption && !previousOption.readiness) {
			sourceOptions[previousMode] = {
				...previousOption,
				readiness: row.readiness,
				reasonCodes: row.reasonCodes,
				sourceModeReason: row.sourceModeReason,
			};
		}
		const sourceOption = sourceOptions[mode];
		if (sourceOption?.available === false) return;
		let sourcePatch: Partial<BidderActionItemMessageRow> = {};
		if (sourceOption) {
			const sourceState = bidderActionItemMessageSourceState({ ...row, sourceOptions }, mode, true);
			sourcePatch = {
				subject: String(sourceOption.subject || ''),
				body: String(sourceOption.body || ''),
				bodyEdited: false,
				readiness: sourceState.readiness,
				reasonCodes: sourceState.reasonCodes,
				sourceModeReason: sourceState.sourceModeReason,
				actionItems: (sourceOption.actionItems || []).map((item) => ({
					...item,
					selected: item.selected !== false,
				})),
			};
		}
		updateRow(rowIndex, {
			sourceMode: mode,
			sourceOptions,
			...sourcePatch,
		});
	};
	const matchingBidderCount = Number(preview.matchingBidderCount);
	const bidderCountLabel = Number.isFinite(matchingBidderCount)
		? `${matchingBidderCount} matching bidder${matchingBidderCount === 1 ? '' : 's'}`
		: `${rows.length} bidder${rows.length === 1 ? '' : 's'}`;

	return (
		<div className="autopilot-bidder-batch" data-testid="bidder-action-item-message-batch">
			<div className="autopilot-bidder-batch__context">
				{preview.projectName && <span>{preview.projectName}</span>}
				{preview.packageName && <span>{preview.packageName}</span>}
				<span>{bidderCountLabel}</span>
			</div>
			{canSwitchPackage && (
				<label className="autopilot-bidder-batch__package-options">
					<span>Bid package</span>
					<select
						aria-label="Bid package"
						disabled={disabled || !editable}
						value={String(preview.bidPackageId || '')}
						onChange={(event) => selectPackage(event.target.value)}
					>
						{packageChoices.map((choice) => (
							<option key={packageChoiceId(choice)} value={packageChoiceId(choice)}>
								{packageChoiceLabel(choice)}
							</option>
						))}
					</select>
					<small>
						Each option was resolved and sealed when this card was created. Changing the package
						loads only that package's exact bidders, contacts, and source items.
					</small>
				</label>
			)}
			{packageChoices.length > 1 && !canSwitchPackage && (
				<p className="autopilot-bidder-batch__package-options">
					<strong>Other possible package matches:</strong>{' '}
					{packageChoices
						.filter((choice) => packageChoiceId(choice) !== String(preview.bidPackageId || ''))
						.map(packageChoiceLabel)
						.join(', ') || 'None'}
					. This historical card remains sealed to {preview.packageName || 'its original package'}.
				</p>
			)}

			{preview.assumptionSummary && (
				<p className="autopilot-bidder-batch__assumption">{preview.assumptionSummary}</p>
			)}
			{Array.isArray(preview.assumptions) && preview.assumptions.length > 0 && (
				<ul className="autopilot-bidder-batch__assumptions" aria-label="Assumptions">
					{preview.assumptions.map((assumption, index) => (
						<li key={`${assumption}-${index}`}>{assumption}</li>
					))}
				</ul>
			)}

			<div className="autopilot-bidder-batch__rows">
				{rows.map((row, rowIndex) => {
					const selected = row.selected !== false;
					const sourceMode = row.sourceMode || 'SEND_CURRENT';
					const sourceState = bidderActionItemMessageSourceState(row);
					const rowReadiness = sourceState.readiness;
					const sourceOptions = row.sourceOptions || {};
					const defaultOption = sourceOptions[sourceMode];
					const currentMessageFrame = safeEditableFrame(
						String(row.body || ''),
						String(defaultOption?.body || row.body || ''),
					);
					return (
						<details
							className={`autopilot-bidder-batch__row is-${rowReadiness.toLowerCase()}`}
							key={String(row.id)}
						>
							<summary>
								{editable ? (
									<input
										aria-label={`Include ${bidderLabel(row, rowIndex)}`}
										checked={selected}
										disabled={disabled}
										onClick={(event) => event.stopPropagation()}
										onChange={(event) => updateRow(rowIndex, { selected: event.target.checked })}
										type="checkbox"
									/>
								) : (
									<span aria-hidden="true" className="autopilot-bidder-batch__selection">
										{selected ? '✓' : '—'}
									</span>
								)}
								<span className="autopilot-bidder-batch__bidder">
									<strong>{bidderLabel(row, rowIndex)}</strong>
									<small>
										{row.sourceMode === 'RESEND_LAST'
											? 'Last successfully sent clarification'
											: 'Current unresolved action items'}
									</small>
								</span>
								<span
									className={`autopilot-bidder-batch__readiness is-${rowReadiness.toLowerCase()}`}
								>
									{readinessLabel(rowReadiness)}
								</span>
								<FaChevronDown aria-hidden="true" />
							</summary>

							<div className="autopilot-bidder-batch__row-body">
								{sourceState.sourceModeReason && <p>{sourceState.sourceModeReason}</p>}
								{Array.isArray(row.assumptions) && row.assumptions.length > 0 && (
									<ul aria-label={`${bidderLabel(row, rowIndex)} assumptions`}>
										{row.assumptions.map((assumption, index) => (
											<li key={`${assumption}-${index}`}>{assumption}</li>
										))}
									</ul>
								)}
								{sourceState.reasonCodes.length > 0 && (
									<p className="autopilot-bidder-batch__reason">
										{sourceState.reasonCodes
											.map((code) => String(code).replace(/_/g, ' '))
											.join(' · ')}
									</p>
								)}

								<label>
									<span>Message source</span>
									<select
										disabled={disabled || !editable || !selected}
										value={row.sourceMode || 'SEND_CURRENT'}
										onChange={(event) =>
											selectSourceMode(
												rowIndex,
												event.target.value as BidderActionItemMessageSourceMode,
											)
										}
									>
										<option
											disabled={sourceOptions.RESEND_LAST?.available === false}
											value="RESEND_LAST"
										>
											Last sent clarification
										</option>
										<option
											disabled={sourceOptions.SEND_CURRENT?.available === false}
											value="SEND_CURRENT"
										>
											Current unresolved action items
										</option>
									</select>
								</label>

								<fieldset className="autopilot-bidder-batch__contacts">
									<legend>Authorized contacts</legend>
									{(row.authorizedContacts || []).map((contact, contactIndex) => (
										<label key={String(contact.id)}>
											<input
												checked={contact.selected !== false}
												disabled={disabled || !editable || !selected}
												onChange={(event) =>
													updateRow(rowIndex, {
														authorizedContacts: (
															row.authorizedContacts || []
														).map((candidate, index) =>
															index === contactIndex
																? { ...candidate, selected: event.target.checked }
																: candidate,
														),
													})
												}
												type="checkbox"
											/>
											<span>{[contact.name, contact.email].filter(Boolean).join(' · ')}</span>
										</label>
									))}
									{!(row.authorizedContacts || []).length && <p>No authorized contacts found.</p>}
								</fieldset>

								<label>
									<span>Subject</span>
									<input
										disabled={disabled || !editable || !selected}
										value={String(row.subject || '')}
										onChange={(event) => updateRow(rowIndex, { subject: event.target.value })}
									/>
								</label>
								{sourceMode === 'RESEND_LAST' ? (
									<label>
										<span>Message</span>
										<textarea
											disabled={disabled || !editable || !selected}
											rows={7}
											value={String(row.body || '')}
											onChange={(event) =>
												updateRow(rowIndex, { body: event.target.value, bodyEdited: true })
											}
										/>
									</label>
								) : (
									<label>
										<span>Message introduction</span>
										<textarea
											aria-label={`${bidderLabel(row, rowIndex)} message introduction`}
											disabled={disabled || !editable || !selected}
											rows={3}
											value={trimBlankEdges(currentMessageFrame.intro).join('\n')}
											onChange={(event) =>
												updateCurrentMessage(rowIndex, row, {
													intro: textLines(event.target.value),
												})
											}
										/>
									</label>
								)}

								{(row.actionItems || []).length > 0 && (
									<fieldset className="autopilot-bidder-batch__items">
										<legend>Included action items</legend>
										{(row.actionItems || []).map((item, itemIndex) => (
											<div className="autopilot-bidder-batch__item-row" key={String(item.id)}>
												<label className="autopilot-bidder-batch__item-selection">
													<input
														checked={item.selected !== false}
														disabled={disabled || !editable || !selected}
														onChange={(event) => {
															const currentItems = actionItemsWithBodyMessages(
																row.actionItems || [],
																String(row.body || ''),
															);
															const actionItems = currentItems.map((candidate, index) =>
																index === itemIndex
																	? { ...candidate, selected: event.target.checked }
																	: candidate,
															);
															updateCurrentMessage(
																rowIndex,
																{ ...row, actionItems },
																{ actionItems },
															);
														}}
														type="checkbox"
													/>
													<span>{actionItemDisplayText(item, `Action item ${itemIndex + 1}`)}</span>
												</label>
												{item.selected !== false && (
													<label className="autopilot-bidder-batch__item-question">
														<span>Question for bidder</span>
														<textarea
															aria-label={`${bidderLabel(
																row,
																rowIndex,
															)} question: ${actionItemDisplayText(
																item,
																`Action item ${itemIndex + 1}`,
															)}`}
															disabled={disabled || !editable || !selected}
															rows={2}
															value={actionItemQuestion(item)}
															onChange={(event) => {
																const currentItems = actionItemsWithBodyMessages(
																	row.actionItems || [],
																	String(row.body || ''),
																);
																const actionItems = currentItems.map((candidate, index) =>
																	index === itemIndex
																		? { ...candidate, messageText: event.target.value }
																		: candidate,
																);
																updateCurrentMessage(
																	rowIndex,
																	{ ...row, actionItems },
																	{ actionItems },
																);
															}}
														/>
													</label>
												)}
											</div>
										))}
									</fieldset>
								)}
								{sourceMode === 'SEND_CURRENT' && (
									<label>
										<span>Message closing</span>
										<textarea
											aria-label={`${bidderLabel(row, rowIndex)} message closing`}
											disabled={disabled || !editable || !selected}
											rows={3}
											value={trimBlankEdges(currentMessageFrame.outro).join('\n')}
											onChange={(event) =>
												updateCurrentMessage(rowIndex, row, {
													outro: textLines(event.target.value),
												})
											}
										/>
									</label>
								)}
							</div>
						</details>
					);
				})}
			</div>
			{editable && bidderActionItemMessageBatchIssue(preview) && (
				<p className="autopilot-bidder-batch__validation" role="status">
					{bidderActionItemMessageBatchIssue(preview)}
				</p>
			)}
		</div>
	);
};

export default BidderActionItemMessageBatch;
